<template>
  <div v-if="showModal"
       class="fixed z-10 inset-0 bg-gray-900 bg-opacity-60 overflow-y-auto flex items-center justify-center text-base">
    <div class="bg-white rounded-lg shadow-xl w-5/12 h-[36vh]">
      <div class="flex items-center justify-between p-4 border-b">
        <h4 class="text-lg text-gray-800 font-bold">Create Scheme</h4>
        <button type="button"
                class="ml-auto -mx-1.5 -my-1.5 p-1.5 inline-flex text-gray-500 items-center justify-end h-8 w-8 hover:text-custom-yellow"
                @click="$emit('close')" aria-label="Close">
          <span class="sr-only">Dismiss</span>
          <svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none"
               viewBox="0 0 14 14">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
          </svg>
        </button>
      </div>

        <div class="grid grid-cols-2 gap-4 m-2">
          <div>
            <label class="block text-sm font-medium text-gray-700">
              Insurance
            </label>
            <v-select
                v-model="form.insuranceId"
                class="mt-2"
                :options="insurances"
                label="ProviderName"
                :filterable="true"
                placeholder="Select an insurance"
                @search="searchInsurances"
                :loading="isSearching"
            >
            </v-select>
          </div>
          <div>
            <label for="input1" class="block text-sm font-medium text-gray-700">
              Scheme Name
            </label>
            <input
                v-model="form.schemeName"
                id="input"
                type="text"
                class="w-full px-3 py-2 mt-3 text-sm bg-gray-100 border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Scheme Name"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">
              Scheme Type
            </label>
            <select
                class="w-full border-gray-300 rounded-lg"
                v-model="form.schemeType"
                @change="checkIfCapitation"
            >
              <option value="" disabled selected>Select Scheme Type</option>
              <option v-for="type in schemeTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
            </select>
          </div>

          <div v-if="isCapitation">
            <label class="block text-sm font-medium text-gray-700">
             Copay Type
            </label>
            <select
                v-model="form.copayType"
                class="w-full border-gray-300 rounded-lg"
            >
              <option value="" disabled selected>Select Scheme Type</option>
              <option v-for="type in copayTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
            </select>
          </div>

          <div v-if="isCapitation">
            <label class="block text-sm font-medium text-gray-700">
             Copay Amount
            </label>
            <input type="number" v-model="form.copayAmount" min="1">
          </div>
        </div>
      <div class="flex justify-center mt-6 gap-4 mx-4">
        <button class="text-center bg-custom-blue text-white px-4 py-2" @click="createScheme">{{ isLoading ? 'Saving..' :  'Save' }}</button>
        <button class="flex justify-center w-full text-center" @click="$emit('close')" ></button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, ref} from "vue";
import apiClient from "@/services/api";
import vSelect from 'vue-select';
import {useToast} from "vue-toast-notification";

// eslint-disable-next-line no-undef
const props = defineProps({
  show: Boolean
});
// Define emits
// eslint-disable-next-line no-undef
const emit = defineEmits(['fetch', 'close']);
const toast = useToast({
  position: 'top-right',
  duration: 3000,
  dismissible: true,
  pauseOnHover: true,
  type: 'default',
});
const isLoading = ref(false);
const isSearching = ref(false);
const insurances =  ref([]);
const schemeTypes = ref([
  { id: 1, name: 'Fee For Service (FFS)' },
  { id: 2, name: 'Capitation' },
  { id: 3, name: 'Cash' },
]);
const isCapitation = ref(false);
const copayTypes = ref([
  { id: 1, name: 'Percentage' },
  { id: 2, name: 'Flat Rate' },
]);
const showModal = computed(() => props.show);
const form = ref({
  insuranceId: null,
  schemeName: '',
  schemeType: null,
  copayType: null,
  copayAmount: null,
});

const checkIfCapitation = () => {
  isCapitation.value = form.value.schemeType === 2; // Assuming 2 is the ID for Capitation
};

const searchInsurances = async (event) => {
  if (!(event.length > 2)) {
    console.log('Search term is too short', event);
    insurances.value = [];
    return;
  }
  const params = {
    searchTerm: event,
  }
  try {
    isSearching.value = true;
    const response = await apiClient.searchInsurance(params);
    if (response.success) {
      insurances.value = response.data;
    }
  } catch (error) {
    isSearching.value = false;
    console.error('Error fetching insurances:', error);
  } finally {
    isSearching.value = false;
  }
}

const createScheme = async () => {
  if (!form.value.insuranceId || !form.value.schemeName || !form.value.schemeType) {
   toast.error('Please fill in all required fields');
    return;
  }
  isLoading.value = true;
  try {
    const response = await apiClient.createScheme({
      insurance_id: form.value.insuranceId,
      name: form.value.schemeName,
      scheme_type: form.value.schemeType,
      copay_type: form.value.copayType,
      copay_amount: form.value.copayAmount,
    });
    if (response.success) {
      toast.success('Scheme created successfully');
      resetForm();
      emit('fetch');
      emit('close');
    } else {
      toast.error('Failed to create scheme: ' + response.message);
    }
  } catch (error) {
    console.error('Error creating scheme:', error);
  } finally {
    isLoading.value = false;
  }
}

const resetForm = () => {
  form.value.insuranceId = null;
  form.value.schemeName = '';
  form.value.schemeType = null;
  form.value.copayType = null;
  form.value.copayAmount = null;
}

</script>

<style scoped>
@import 'vue-select/dist/vue-select.css';

</style>